/**
 * Shared plumbing for per-block render modules (NOS-151).
 *
 * Block modules live in this directory, one file per block type, and receive
 * BlockComponentProps. They must not import from website-builder editor code —
 * this render layer stays portable for the public-site replatform (M22).
 */
import type { CSSProperties, ReactNode } from "react"
import type { OrgCustomizations, Organization, SectionStyleOverrides } from "@/templates/types"
import type { BlockStyleOverrides } from "@/components/website-builder/blocks/types"
import { SectionBackgroundWrapper } from "@/components/website-builder/SectionBackgroundWrapper"
import { tiptapJsonToHtml } from "@/lib/tiptap-html"
import { sanitizeHtml, safeHref } from "@/lib/sanitizeHtml"

/** NOS-119: Tailwind class maps to avoid purge issues */
const CORNER_MAP: Record<string, string> = {
  none: "rounded-none",
  sm: "rounded-sm",
  md: "rounded-md",
  lg: "rounded-lg",
  full: "rounded-full",
}
const PADDING_MAP: Record<string, string> = {
  compact: "py-4",
  normal: "py-8",
  spacious: "py-16",
}
const ALIGN_MAP: Record<string, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
}
/** NOS-154: section width override — absent width key = full width (zero change to output) */
const WIDTH_MAP: Record<string, string> = {
  contained: "max-w-5xl mx-auto",
}

export interface ResolvedBlockStyles {
  cornerClass: string
  paddingClass: string
  bgStyle: Record<string, string> | undefined
  alignClass: string
  widthClass: string
  sectionClassName: string
  sectionStyle: Record<string, string> | undefined
  hasOverrides: boolean
}

/** NOS-119: Resolve content._styles to Tailwind classes and inline styles.
 * Returns empty values when _styles absent so existing blocks render unchanged. */
export function resolveBlockStyles(content: Record<string, unknown> | null): ResolvedBlockStyles {
  const s = content?._styles as BlockStyleOverrides | undefined
  if (!s || Object.keys(s).length === 0) {
    return { cornerClass: "", paddingClass: "", bgStyle: undefined, alignClass: "", widthClass: "", sectionClassName: "", sectionStyle: undefined, hasOverrides: false }
  }
  const cornerClass = s.corner_radius ? (CORNER_MAP[s.corner_radius] ?? "") : ""
  const paddingClass = s.padding ? (PADDING_MAP[s.padding] ?? "") : ""
  const bgStyle =
    s.background !== undefined && s.background !== "transparent"
      ? { backgroundColor: s.background }
      : s.background === "transparent"
        ? { backgroundColor: "transparent" }
        : undefined
  const alignClass = s.text_align ? (ALIGN_MAP[s.text_align] ?? "") : ""
  const widthClass = s.width ? (WIDTH_MAP[s.width] ?? "") : ""
  const sectionClassName = [cornerClass, paddingClass, alignClass, widthClass].filter(Boolean).join(" ")
  return { cornerClass, paddingClass, bgStyle, alignClass, widthClass, sectionClassName, sectionStyle: bgStyle, hasOverrides: true }
}

/** NOS-236: hero height option — public vh per option, builder px so the canvas stays compact. */
const HERO_HEIGHT_VH: Record<string, string> = { full: "100vh", "70": "70vh", "50": "50vh" }
const HERO_HEIGHT_BUILDER: Record<string, number> = { full: 520, "70": 400, "50": 300 }

/**
 * NOS-236: resolve the hero's minHeight from `content.hero_height`
 * ("full" | "70" | "50"). Absent/unknown values return the caller's default,
 * so every existing hero renders exactly as before.
 */
export function resolveHeroMinHeight(
  content: Record<string, unknown> | null,
  inBuilder: boolean,
  publicDefault: string | undefined,
  builderDefault: number
): string | number | undefined {
  const h = content?.hero_height
  if (typeof h === "string") {
    if (inBuilder && HERO_HEIGHT_BUILDER[h] !== undefined) return HERO_HEIGHT_BUILDER[h]
    if (!inBuilder && HERO_HEIGHT_VH[h] !== undefined) return HERO_HEIGHT_VH[h]
  }
  return inBuilder ? builderDefault : publicDefault
}

/**
 * NOS-236: per-page scroll-arrow default. An explicit `content.scroll_arrow`
 * boolean always wins; otherwise the arrow shows on the homepage only. When
 * the renderer has no page context (isHomePage undefined) the legacy
 * always-on behavior is preserved.
 */
export function resolveScrollArrow(
  content: Record<string, unknown> | null,
  isHomePage: boolean | undefined
): boolean {
  const v = content?.scroll_arrow
  if (typeof v === "boolean") return v
  return isHomePage ?? true
}

/** Resolve per-item BlockStyleOverrides to inline CSS properties. */
export function resolveItemStyle(styles: BlockStyleOverrides | undefined): CSSProperties {
  if (!styles || Object.keys(styles).length === 0) return {}
  const result: CSSProperties = {}
  if (styles.background) result.background = styles.background
  if (styles.text_color) result.color = styles.text_color
  if (styles.text_align) result.textAlign = styles.text_align
  const pMap: Record<string, string> = { compact: "12px", normal: "24px", spacious: "40px" }
  if (styles.padding) result.padding = pMap[styles.padding] ?? undefined
  const rMap: Record<string, string> = { none: "0", sm: "4px", md: "8px", lg: "16px", full: "9999px" }
  if (styles.corner_radius) result.borderRadius = rMap[styles.corner_radius] ?? undefined
  return result
}

/** Render paragraph block (TipTap JSON) to HTML */
export function renderParagraphContent(content: unknown): string {
  if (!content || typeof content !== "object") return ""
  return tiptapJsonToHtml(content)
}

/** Extract YouTube/Vimeo embed URL from watch/share URL */
export function getVideoEmbedUrl(url: string): string | null {
  if (!url || typeof url !== "string") return null
  const u = url.trim()
  const ytMatch = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/)
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`
  const vimeoMatch = u.match(/vimeo\.com\/(?:video\/)?(\d+)/)
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`
  if (u.startsWith("https://") && (u.includes("youtube.com/embed") || u.includes("player.vimeo.com")))
    return u
  return null
}

/** Only allow https:// URLs for iframe src (embed/map) */
export function isHttpsUrl(url: string): boolean {
  return typeof url === "string" && url.startsWith("https://")
}

/**
 * NOS-237: direct video-file URL playable by a native <video> element.
 * Weebly (and friends) host section videos as plain mp4 uploads — imports
 * carry the file URL, which the YouTube/Vimeo embed path can't play.
 */
export function isDirectVideoUrl(url: string): boolean {
  if (typeof url !== "string" || !url.startsWith("https://")) return false
  try {
    return /\.(mp4|webm|ogv|m4v|mov)$/i.test(new URL(url).pathname)
  } catch {
    return false
  }
}

/** NOS-116: Builder-only metadata keys to strip for public rendering.
 * NOS-219/NOS-214: `_synthesized` is the import-provenance marker (block not
 * anchored to source DOM) — review-UI metadata, never rendered publicly. */
const BUILDER_ONLY_KEYS = ["import_source", "_synthesized"]

export function sanitizeContentForPublic(content: Record<string, unknown> | null): Record<string, unknown> | null {
  if (!content) return null
  const sanitized = { ...content }
  for (const key of BUILDER_ONLY_KEYS) {
    delete sanitized[key]
  }
  return sanitized
}

/** Renders a single element from a section's `blocks` array (added via Add Element catalog) */
export function GenericExtraBlock({ block }: { block: { id: string; type: string; content: Record<string, unknown> } }) {
  const { type, content } = block
  // _styles is stored on the block object itself (not block.content) by updateElStyle
  const blockStyles = resolveItemStyle((block as Record<string, unknown>)._styles as BlockStyleOverrides | undefined)
  switch (type) {
    case "heading": {
      const level = Math.min(2, Math.max(1, (content.level as number) ?? 1))
      const Tag = `h${level}` as keyof JSX.IntrinsicElements
      const sizeClass = level === 1 ? "text-3xl font-bold tracking-tight @sm:text-4xl" : "text-2xl font-bold tracking-tight @sm:text-3xl"
      const customClass = (block as Record<string, unknown>)._styles ? ((block as Record<string, unknown>)._styles as BlockStyleOverrides).custom_class : undefined
      return <Tag className={[sizeClass, customClass].filter(Boolean).join(" ")} style={blockStyles}>{(content.text as string) ?? ""}</Tag>
    }
    case "subheading": {
      const level = Math.min(5, Math.max(3, (content.level as number) ?? 3))
      const Tag = `h${level}` as keyof JSX.IntrinsicElements
      const subSizeClass = level === 3 ? "text-xl font-semibold tracking-tight @sm:text-2xl"
        : level === 4 ? "text-lg font-semibold tracking-tight @sm:text-xl"
        : "text-base font-semibold tracking-tight @sm:text-lg"
      const customClass = (block as Record<string, unknown>)._styles ? ((block as Record<string, unknown>)._styles as BlockStyleOverrides).custom_class : undefined
      return <Tag className={[subSizeClass, customClass].filter(Boolean).join(" ")} style={blockStyles}>{(content.text as string) ?? ""}</Tag>
    }
    case "paragraph": {
      const customClass = (block as Record<string, unknown>)._styles ? ((block as Record<string, unknown>)._styles as BlockStyleOverrides).custom_class : undefined
      return (
        <p
          className={[`leading-relaxed${blockStyles.color ? "" : " text-muted-foreground"}`, customClass].filter(Boolean).join(" ")}
          style={blockStyles}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml((content.text as string) ?? "") }}
        />
      )
    }
    case "button": {
      const customClass = (block as Record<string, unknown>)._styles ? ((block as Record<string, unknown>)._styles as BlockStyleOverrides).custom_class : undefined
      return (
        <a
          href={safeHref(content.url as string) ?? "#"}
          className={["inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground", customClass].filter(Boolean).join(" ")}
          style={blockStyles}
        >
          {(content.text as string) ?? "Button"}
        </a>
      )
    }
    case "image":
      return (content.src as string) ? (
        <img src={content.src as string} alt={(content.alt as string) ?? ""} className="max-w-full rounded-md" />
      ) : null
    case "divider":
      return <hr className="border-border" />
    case "spacer":
      return <div style={{ height: (content.height as number) ?? 32 }} />
    case "stat":
      return (
        <div className="text-center">
          <div className="text-2xl font-bold">{(content.value as string) ?? "0"}</div>
          <div className="text-sm text-muted-foreground">{(content.label as string) ?? ""}</div>
        </div>
      )
    case "testimonial":
      return (
        <blockquote className="border-l-4 border-primary pl-4 italic">
          <p>{(content.quote as string) ?? ""}</p>
          {typeof content.author === "string" && content.author ? (
            <cite className="mt-2 block text-sm not-italic text-muted-foreground">— {content.author}</cite>
          ) : null}
        </blockquote>
      )
    case "card":
      return (
        <div className="rounded-md border border-border p-4">
          {typeof content.title === "string" && content.title ? (
            <h3 className="font-semibold">{content.title}</h3>
          ) : null}
          {typeof content.text === "string" && content.text ? (
            <p className="mt-1 text-sm text-muted-foreground">{content.text}</p>
          ) : null}
        </div>
      )
    case "icon-text":
      return (
        <div className="flex items-start gap-2">
          <span>{(content.icon as string) ?? "★"}</span>
          <span>{(content.text as string) ?? ""}</span>
        </div>
      )
    case "list-item":
      return <li className="leading-relaxed">{(content.text as string) ?? ""}</li>
    default:
      return null
  }
}

export interface BlockComponentProps {
  /** Section content, already sanitized for public rendering by the dispatcher. */
  content: Record<string, unknown> | null
  customizations: OrgCustomizations
  org?: Organization | null
  inBuilder: boolean
  /** Resolved content._styles as {className, style}, undefined when no overrides. */
  styleOverrides?: SectionStyleOverrides
  /** Full resolveBlockStyles(content) result — blocks read paddingClass/cornerClass/alignClass/sectionStyle/hasOverrides. */
  resolved: ResolvedBlockStyles
  /** section.id — needed by blocks that submit to edge functions (form, contact, newsletter_signup). */
  sectionId: string
  /** NOS-236: whether the section renders on the homepage (scroll-arrow default). Undefined = unknown context. */
  isHomePage?: boolean
}

/** Wrap with background image+overlay when content.background_image exists (non-hero). */
export function wrapWithBackgroundIfNeeded(
  content: Record<string, unknown> | null,
  children: ReactNode
): ReactNode {
  const bgUrl = content?.background_image as string | undefined
  if (!bgUrl) return children
  return (
    <SectionBackgroundWrapper
      backgroundImage={bgUrl}
      backgroundImageAlt={(content?.background_image_alt as string) ?? ""}
      overlayOpacity={(content?.background_overlay_opacity as number) ?? 0.55}
    >
      {children}
    </SectionBackgroundWrapper>
  )
}

/**
 * NOS-180 (July 2026 bench design rule): section headings render in a dark
 * neutral, never the brand hue — brand-tinted headings everywhere made every
 * imported site read as "default blue theme." The var comes from
 * tokensToCssVars (resolved to the token text color); the fallback matches
 * the resolver's dark text.
 */
export const HEADING_COLOR = "var(--nos-heading-color, #0f172a)"

/**
 * Plain-text projection of a rich-text field. Body copy is edited as HTML
 * (RichTextInput) and stored strings may carry tags and entities (typed
 * &nbsp;, imported markup). Sections that render body as a plain string must
 * never show them raw.
 */
export function textFromRichHtml(value: unknown): string {
  if (typeof value !== "string") return ""
  const text = value
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;|\u00a0/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, "'")
    .replace(/&amp;/g, "&")
    .trim()
  return healLetterSpacedText(text)
}

/**
 * Source sites sometimes fake letter-spacing by typing spaces between letters
 * ("D O N ' T  C U R S E", nbsp marking word gaps) \u2014 imported verbatim, and
 * once entities decode to plain spaces the word gaps collapse into the letter
 * gaps. When a string is clearly letter-spaced (nearly all single-char
 * tokens) AND the word boundaries are still recoverable (runs of 2+ spaces),
 * rebuild the words. Strings without recoverable boundaries are left alone.
 */
export function healLetterSpacedText(text: string): string {
  if (!/ {2,}/.test(text)) return text
  const tokens = text.split(/\s+/)
  if (tokens.length < 8) return text
  const singleChar = tokens.filter((t) => t.length === 1).length
  if (singleChar / tokens.length < 0.8) return text
  return text
    .split(/ {2,}/)
    .map((word) => word.replace(/ /g, ""))
    .join(" ")
}
